// pages/UserAdmin/crm/QualificacaoContato.js — a seção "Qualificação" da
// janela do contato (out/26, rastreamento PR 2).
//
// Três botões lado a lado — Qualificado, Desqualificado, Venda fechada —
// com o atual aceso. Qualificado grava no clique; Desqualificado pede o
// motivo e Venda o valor, no MESMO painel do chat e do lote
// (components/qualificacao). Embaixo: o detalhe da marcação, quem marcou e
// quando, "Remover marcação" e o histórico. Depois de marcar, "Desfazer"
// fica 10 s na tela.

import { useState } from 'react';
import { Box } from '@mui/material';
import api from '../../../services/Api';
import AvisoDesfazer from '../../../components/qualificacao/AvisoDesfazer';
import PainelQualificar from '../../../components/qualificacao/PainelQualificar';
import useQualificar from '../../../components/qualificacao/useQualificar';
import { ORDEM, STATUS, descrever } from '../../../components/qualificacao/regras';
import { OUTFIT, rotulo } from './theme';

const foco = { '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } };

// Rastreamento PR 3: a marcação já foi pra Meta? (só lead de anúncio com o
// clique guardado e com o envio ligado na Administração Meta → Anúncios.)
function naMeta(meta) {
  if (!meta?.length) return '';
  if (meta.every((m) => m.status === 'enviado')) return 'enviado à Meta';
  if (meta.some((m) => ['pendente', 'falhou', 'enviando'].includes(m.status))) return 'na fila da Meta';
  return 'não foi à Meta';
}
const link = {
  ...foco, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', borderRadius: '6px',
  fontFamily: OUTFIT, fontSize: 13.5, fontWeight: 600, color: 'var(--ch-text2)', '&:hover': { color: 'var(--ch-text)' },
  '&:disabled': { opacity: 0.5, cursor: 'default' },
};

export default function QualificacaoContato({ contato, cartao, fmtDate, onMudou }) {
  const q = contato?.qualificacao || null;
  const atual = q?.status || null;
  const [painel, setPainel] = useState(null); // { anchor, passo }
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  // null = fechado; [] em diante = aberto.
  const [historico, setHistorico] = useState(null);

  const recarregarHistorico = async (forcar = false) => {
    if (historico === null && !forcar) return;
    try {
      const { data } = await api.get(`/contacts/${contato.id}/qualificacoes`);
      setHistorico(data.items || []);
    } catch {
      setHistorico([]);
    }
  };

  const { marcar, desfazer, erroDesfazer, desfazerAgora, fecharDesfazer } = useQualificar((id, nova) => {
    onMudou?.(id, nova);
    recarregarHistorico();
  });

  const escolher = async (pedido) => {
    setErro('');
    await marcar(contato.id, pedido);
  };

  const direto = async (pedido) => {
    setSalvando(true);
    try {
      await escolher(pedido);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const clicar = (k, e) => {
    if (k === 'qualificado') {
      if (atual !== 'qualificado') direto({ status: 'qualificado' });
      return;
    }
    setPainel({ anchor: e.currentTarget, passo: k });
  };

  return (
    <Box>
      <Box component="span" sx={{ ...rotulo, display: 'block', mb: '12px' }}>Qualificação</Box>
      <Box sx={{ ...cartao, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box role="group" aria-label="Marcar o lead" sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {ORDEM.map((k) => {
            const t = STATUS[k];
            const aceso = atual === k;
            return (
              <Box key={k} component="button" type="button" aria-pressed={aceso} disabled={salvando}
                onClick={(e) => clicar(k, e)}
                sx={{
                  ...foco, flex: '1 1 auto', height: 38, px: '12px', borderRadius: '10px', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                  fontFamily: OUTFIT, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap',
                  border: `1px solid ${aceso ? t.borda : 'var(--ch-bdr)'}`,
                  bgcolor: aceso ? t.bg : 'var(--ch-card)', color: aceso ? t.cor : 'var(--ch-text2)',
                  transition: 'border-color .15s, color .15s, background-color .15s',
                  '&:hover': { borderColor: t.borda, color: t.cor },
                  '&:disabled': { opacity: 0.6, cursor: 'default' },
                }}>
                <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: aceso ? t.ponto : 'var(--ch-bdr2)' }} />
                {t.rotulo}
              </Box>
            );
          })}
        </Box>

        {q ? (
          <Box sx={{ fontSize: 13.5, color: 'var(--ch-text2)', lineHeight: 1.5 }}>
            <Box component="span" sx={{ color: 'var(--ch-text)', fontWeight: 600 }}>{descrever(q)}</Box>
            {(q.por || q.em) && (
              <Box component="span" sx={{ color: 'var(--cx-cap)' }}>
                {' · '}{q.por ? `por ${q.por}` : ''}{q.por && q.em ? ' · ' : ''}{q.em ? fmtDate(q.em) : ''}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ fontSize: 13.5, color: 'var(--cx-cap)' }}>
            Ninguém marcou este lead ainda.
          </Box>
        )}

        {erro && <Box role="alert" sx={{ fontSize: 13, color: 'var(--ch-red-d)' }}>{erro}</Box>}
        <AvisoDesfazer aviso={desfazer} erro={erroDesfazer} onDesfazer={desfazerAgora} onFechar={fecharDesfazer} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
          {q && (
            <Box component="button" type="button" sx={link} disabled={salvando} onClick={() => direto({ status: null })}>
              Remover marcação
            </Box>
          )}
          <Box component="button" type="button" sx={link} aria-expanded={historico !== null}
            onClick={() => (historico === null ? recarregarHistorico(true) : setHistorico(null))}>
            {historico === null ? 'Ver histórico' : 'Ocultar histórico'}
          </Box>
        </Box>

        {historico !== null && (
          historico.length === 0 ? (
            <Box sx={{ fontSize: 13, color: 'var(--cx-cap)' }}>Nenhuma marcação ainda.</Box>
          ) : (
            <Box component="ul" aria-label="Histórico de qualificação" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {historico.map((h) => (
                <Box component="li" key={h.id} sx={{
                  py: '8px', borderTop: '1px solid var(--ch-bdr)', display: 'flex', flexWrap: 'wrap',
                  alignItems: 'baseline', gap: '2px 10px', fontSize: 13,
                }}>
                  <Box component="span" sx={{
                    fontWeight: 600, color: h.desfeita ? 'var(--cx-cap)' : 'var(--ch-text)',
                    textDecoration: h.desfeita ? 'line-through' : 'none',
                  }}>
                    {h.status === 'removido' ? 'Marcação removida' : descrever(h)}
                  </Box>
                  <Box component="span" sx={{ color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
                    {h.por || 'Alguém'} · {fmtDate(h.em)}{h.desfeita ? ' · desfeita' : ''}
                    {naMeta(h.meta) && ` · ${naMeta(h.meta)}`}
                  </Box>
                </Box>
              ))}
            </Box>
          )
        )}
      </Box>

      <PainelQualificar
        anchorEl={painel?.anchor || null}
        passoInicial={painel?.passo || 'inicio'}
        atual={atual}
        onClose={() => setPainel(null)}
        onEscolher={escolher}
      />
    </Box>
  );
}
